import { api } from './client';

export interface MeetingPrepTab {
  tabId: string;
  url: string;
  title: string;
  purpose: string;
}

export interface MeetingPrepResource {
  id: string;
  type: string;
  title: string;
  url?: string;
  relevance: number;
}

export interface MeetingPrepSuggestion {
  id: string;
  category: string;
  text: string;
  priority: string;
}

export interface MeetingPrepSessionSummary {
  id: string;
  meetingTitle: string;
  attendees: string[];
  agenda: string;
  scheduledAt: string;
  createdAt: string;
  platform?: string;
  durationMinutes?: number;
  meetingUrl?: string;
  notes?: string;
}

export interface MeetingPrepSessionDetail extends MeetingPrepSessionSummary {
  tabs: MeetingPrepTab[];
  resources: MeetingPrepResource[];
  suggestions: MeetingPrepSuggestion[];
  preparationScore: number;
}

export interface MeetingPrepTemplate {
  id: string;
  name: string;
  description: string;
}

export interface CreateMeetingPrepPayload {
  meetingTitle: string;
  attendees?: string[];
  agenda?: string;
  scheduledAt?: string;
  platform?: string;
  durationMinutes?: number;
  meetingUrl?: string;
}

export interface UpdateMeetingPrepPayload {
  meetingTitle: string;
  attendees?: string[];
  agenda?: string;
  platform?: string;
  meetingUrl?: string;
  durationMinutes?: number;
  scheduledAt?: string;
  notes?: string;
}

interface RawTab {
  tab_id?: string;
  url?: string;
  title?: string;
  purpose?: string;
}

interface RawResource {
  id?: string;
  type?: string;
  title?: string;
  url?: string;
  relevance?: number;
}

interface RawSuggestion {
  id?: string;
  category?: string;
  text?: string;
  priority?: string;
}

interface RawSession {
  id?: string;
  meeting_title?: string;
  attendees?: string[];
  agenda?: string;
  scheduled_at?: string;
  created_at?: string;
  platform?: string;
  duration_minutes?: number;
  meeting_url?: string;
  notes?: string;
  tabs?: RawTab[];
  resources?: RawResource[];
  suggestions?: RawSuggestion[];
  preparation_score?: number;
}

function mapTab(raw: RawTab): MeetingPrepTab {
  return {
    tabId: String(raw.tab_id ?? ''),
    url: String(raw.url ?? ''),
    title: String(raw.title ?? ''),
    purpose: String(raw.purpose ?? 'reference'),
  };
}

function mapResource(raw: RawResource): MeetingPrepResource {
  const rel = Number(raw.relevance ?? 0);
  return {
    id: String(raw.id ?? ''),
    type: String(raw.type ?? 'document'),
    title: String(raw.title ?? ''),
    url: raw.url ? String(raw.url) : undefined,
    relevance: Number.isFinite(rel) ? Math.round(rel) : 0,
  };
}

function mapSuggestion(raw: RawSuggestion): MeetingPrepSuggestion {
  return {
    id: String(raw.id ?? ''),
    category: String(raw.category ?? 'prep'),
    text: String(raw.text ?? ''),
    priority: String(raw.priority ?? 'medium'),
  };
}

function mapSummary(raw: RawSession): MeetingPrepSessionSummary {
  return {
    id: String(raw.id ?? ''),
    meetingTitle: String(raw.meeting_title ?? ''),
    attendees: Array.isArray(raw.attendees) ? raw.attendees.map(String) : [],
    agenda: String(raw.agenda ?? ''),
    scheduledAt: String(raw.scheduled_at ?? ''),
    createdAt: String(raw.created_at ?? ''),
    platform: raw.platform ? String(raw.platform) : undefined,
    durationMinutes: raw.duration_minutes != null ? Number(raw.duration_minutes) : undefined,
    meetingUrl: raw.meeting_url ? String(raw.meeting_url) : undefined,
    notes: raw.notes ? String(raw.notes) : undefined,
  };
}

function mapDetail(raw: RawSession): MeetingPrepSessionDetail {
  const base = mapSummary(raw);
  return {
    ...base,
    tabs: Array.isArray(raw.tabs) ? raw.tabs.map(mapTab) : [],
    resources: Array.isArray(raw.resources) ? raw.resources.map(mapResource) : [],
    suggestions: Array.isArray(raw.suggestions) ? raw.suggestions.map(mapSuggestion) : [],
    preparationScore: Math.min(
      100,
      Math.max(0, Math.round(Number(raw.preparation_score ?? 0))),
    ),
  };
}

export const meetingPreparationService = {
  async listTemplates(): Promise<MeetingPrepTemplate[]> {
    const { data } = await api.get<{ templates?: MeetingPrepTemplate[] }>('/meeting-prep/templates');
    return data.templates ?? [];
  },

  async listSessions(): Promise<MeetingPrepSessionSummary[]> {
    const { data } = await api.get<{ sessions?: RawSession[] }>('/meeting-prep/sessions');
    const rows = data.sessions ?? [];
    return rows.map(mapSummary);
  },

  async createSession(payload: CreateMeetingPrepPayload): Promise<MeetingPrepSessionSummary> {
    const body: Record<string, unknown> = {
      meeting_title: payload.meetingTitle,
      attendees: payload.attendees ?? [],
      agenda: payload.agenda ?? '',
    };
    if (payload.scheduledAt) body.scheduled_at = payload.scheduledAt;
    if (payload.platform != null && payload.platform !== '') body.platform = payload.platform;
    if (payload.durationMinutes != null && payload.durationMinutes > 0) {
      body.duration_minutes = payload.durationMinutes;
    }
    if (payload.meetingUrl != null && payload.meetingUrl !== '') body.meeting_url = payload.meetingUrl;

    const { data } = await api.post<RawSession>('/meeting-prep/sessions', body);
    return mapSummary(data);
  },

  async getSession(id: string): Promise<MeetingPrepSessionDetail> {
    const { data } = await api.get<RawSession>(`/meeting-prep/sessions/${encodeURIComponent(id)}`);
    return mapDetail(data);
  },

  async updateSession(id: string, payload: UpdateMeetingPrepPayload): Promise<MeetingPrepSessionDetail> {
    const body: Record<string, unknown> = {
      meeting_title: payload.meetingTitle,
      attendees: payload.attendees ?? [],
      agenda: payload.agenda ?? '',
      platform: payload.platform ?? '',
      meeting_url: payload.meetingUrl ?? '',
      notes: payload.notes ?? '',
    };
    if (payload.durationMinutes != null && payload.durationMinutes > 0) {
      body.duration_minutes = payload.durationMinutes;
    }
    if (payload.scheduledAt) body.scheduled_at = payload.scheduledAt;
    const { data } = await api.put<RawSession>(`/meeting-prep/sessions/${encodeURIComponent(id)}`, body);
    return mapDetail(data);
  },

  async deleteSession(id: string): Promise<void> {
    await api.delete(`/meeting-prep/sessions/${encodeURIComponent(id)}`);
  },

  async prepareSession(id: string): Promise<MeetingPrepSessionDetail> {
    const { data } = await api.post<RawSession>(
      `/meeting-prep/sessions/${encodeURIComponent(id)}/prepare`,
      {},
    );
    return mapDetail(data);
  },

  async applyTemplate(id: string, templateId: string): Promise<MeetingPrepSessionDetail> {
    const { data } = await api.post<RawSession>(
      `/meeting-prep/sessions/${encodeURIComponent(id)}/apply-template`,
      { template_id: templateId },
    );
    return mapDetail(data);
  },
};
